import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import api from '../lib/api'

function formatDateTime(value) {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR')
}

function formatCountdown(target) {
  if (!target) return '00:00'
  const distance = Math.max(0, new Date(target).getTime() - Date.now())
  const totalMinutes = Math.floor(distance / 60000)
  const seconds = Math.floor((distance % 60000) / 1000)
  return `${String(totalMinutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export default function VideoCallPage({ user }) {
  const { appointmentId } = useParams()
  const [appointment, setAppointment] = useState(null)
  const [controls, setControls] = useState({ canStart: false, canEnd: false })
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [countdown, setCountdown] = useState('00:00')

  const loadSession = async (showErrors = true) => {
    try {
      setLoading(true)
      const { data } = await api.get(`/appointments/${appointmentId}/video-session`)
      setAppointment(data.appointment)
      setControls(data.controls || { canStart: false, canEnd: false })
      setError('')
    } catch (err) {
      if (showErrors) setError(err.response?.data?.error || 'Não foi possível carregar a sessão agora.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!appointmentId) return
    loadSession()
  }, [appointmentId])

  useEffect(() => {
    if (!appointment?.sessionWindow?.endsAt) return undefined
    const tick = () => setCountdown(formatCountdown(appointment.sessionWindow.endsAt))
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [appointment?.sessionWindow?.endsAt])

  const handleStart = async () => {
    try {
      setBusy(true)
      const { data } = await api.post(`/appointments/${appointmentId}/video-session/start`)
      setAppointment(data.appointment)
      setControls(data.controls || { canStart: true, canEnd: true })
      setInfo(data.message || 'Sessão iniciada.')
      setError('')
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível iniciar a sessão.')
    } finally {
      setBusy(false)
    }
  }

  const handleEnd = async () => {
    try {
      setBusy(true)
      const { data } = await api.post(`/appointments/${appointmentId}/video-session/end`)
      setAppointment(data.appointment)
      setInfo(data.message || 'Sessão encerrada.')
      setError('')
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível encerrar a sessão.')
    } finally {
      setBusy(false)
    }
  }

  const jitsiUrl = useMemo(() => {
    if (!appointment?.meetingId) return ''
    return `https://meet.jit.si/${encodeURIComponent(appointment.meetingId)}#config.prejoinPageEnabled=false&config.startWithAudioMuted=false&config.startWithVideoMuted=false`
  }, [appointment?.meetingId])

  if (!user) return <div className="alert">Faça login para entrar na videochamada.</div>
  if (loading) return <div className="loading-screen">Carregando sessão de videochamada...</div>

  const canJoin = Boolean(appointment?.videoCallLink && appointment?.sessionAccess?.canJoin)

  return (
    <div className="page-stack">
      <section className="section-title-wrap">
        <span className="eyebrow">Videochamada protegida</span>
        <h1>Sala do atendimento on-line</h1>
        <p>Usuário e profissional entram pelo mesmo fluxo, com duração máxima controlada pelo agendamento.</p>
      </section>

      {info ? <div className="alert">{info}</div> : null}
      {error ? <div className="alert" style={{ borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c' }}>{error}</div> : null}

      {appointment ? (
        <div className="grid cols-2">
          <div className="surface-card">
            <div className="section-head">
              <div>
                <h3>Resumo da sessão</h3>
                <p>{appointment.professionalName} com {appointment.userName}</p>
              </div>
            </div>
            <div className="stack-sm">
              <p><strong>Data:</strong> {new Date(appointment.appointmentDate).toLocaleDateString('pt-BR')} às {String(appointment.appointmentTime).slice(0, 5)}</p>
              <p><strong>Status:</strong> {appointment.status}</p>
              <p><strong>Tempo máximo:</strong> {appointment.durationMinutes} minutos</p>
              <p><strong>Entrada liberada a partir de:</strong> {formatDateTime(appointment.sessionWindow?.canJoinFrom)}</p>
              <p><strong>Encerramento automático previsto:</strong> {formatDateTime(appointment.sessionWindow?.endsAt)}</p>
              <p><strong>Tempo restante da sessão:</strong> {countdown}</p>
              {appointment.meetingId ? <p><strong>Sala:</strong> {appointment.meetingId}</p> : null}
            </div>
            <div className="button-wrap" style={{ marginTop: 16 }}>
              {controls.canStart && !appointment.videoCallLink ? <button className="btn btn-primary" disabled={busy} onClick={handleStart}>{busy ? 'Iniciando...' : 'Iniciar sessão'}</button> : null}
              {controls.canStart && appointment.videoCallLink && appointment.status !== 'completed' ? <button className="btn btn-secondary" disabled={busy} onClick={loadSession}>{busy ? 'Atualizando...' : 'Atualizar sessão'}</button> : null}
              {controls.canEnd && appointment.status !== 'completed' ? <button className="btn btn-danger" disabled={busy} onClick={handleEnd}>{busy ? 'Encerrando...' : 'Encerrar sessão'}</button> : null}
              <Link className="btn btn-ghost" to="/perfil">Voltar ao perfil</Link>
            </div>
          </div>

          <div className="surface-card">
            <div className="section-head">
              <div>
                <h3>Acesso à chamada</h3>
                <p>O profissional pode iniciar antes. O usuário entra quando a sessão estiver liberada.</p>
              </div>
            </div>
            {!appointment.videoCallLink ? (
              <div className="alert">A sala ainda não foi iniciada pelo profissional responsável.</div>
            ) : appointment.sessionWindow?.isTooEarly ? (
              <div className="alert">A entrada será liberada 15 minutos antes do horário agendado.</div>
            ) : appointment.sessionWindow?.isExpired || appointment.status === 'completed' ? (
              <div className="alert">A sessão foi encerrada ou ultrapassou o tempo permitido.</div>
            ) : canJoin ? (
              <div className="stack-sm">
                <p><strong>Link interno:</strong> <span className="muted">{appointment.videoCallLink}</span></p>
                <iframe
                  title={`Videochamada ${appointment.meetingId || appointment.id}`}
                  src={jitsiUrl}
                  style={{ width: '100%', minHeight: 560, border: '1px solid var(--line)', borderRadius: 18 }}
                  allow="camera; microphone; fullscreen; display-capture"
                />
              </div>
            ) : (
              <div className="alert">Aguardando liberação da sala.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
